import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const Auth = () => {
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [company, setCompany] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { signIn, signUp, resetPassword } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (mode === "signin") {
        await signIn(email, password);
        toast.success("Signed in successfully");
        navigate("/client-portal");
      } else if (mode === "signup") {
        await signUp(email, password, displayName, company);
        toast.success("Account created. Please check your email to confirm.");
      } else {
        await resetPassword(email);
        toast.success("Password reset email sent.");
      }
    } catch (err: any) {
      const SAFE_ERRORS: Record<string, string> = {
        'User already registered': 'Unable to create account. Please try signing in.',
        'Invalid login credentials': 'Incorrect email or password.',
        'Email rate limit exceeded': 'Too many attempts. Please try again later.',
      };
      toast.error(SAFE_ERRORS[err.message] ?? 'An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <PageHead title="Client Login" description="Sign in to the Batts Legal LLP Client Portal." path="/auth" />
      <section className="bg-hero text-primary-foreground py-20">
        <div className="editorial-container">
          <div className="gold-accent-line mb-6" />
          <h1 className="text-4xl md:text-5xl font-serif font-semibold mb-4">
            {mode === "signin" ? "Client Login" : mode === "signup" ? "Request Access" : "Reset Password"}
          </h1>
          <p className="text-lg opacity-80 max-w-2xl">
            {mode === "signin" ? "Access the secure client engagement portal." : mode === "signup" ? "Create an account to access the Client Portal." : "Enter your email to receive a password reset link."}
          </p>
        </div>
      </section>

      <section className="section-padding">
        <div className="editorial-container max-w-md">
          <div className="bg-card rounded-lg p-10 shadow-elevated border border-border">
            <form onSubmit={handleSubmit} className="space-y-4">
              {mode === "signup" && (
                <>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Full Name</label>
                    <input value={displayName} onChange={e => setDisplayName(e.target.value)} required className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-background text-foreground" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-2">Company</label>
                    <input value={company} onChange={e => setCompany(e.target.value)} className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-background text-foreground" />
                  </div>
                </>
              )}
              <div>
                <label className="text-sm font-medium text-foreground block mb-2">Email</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-background text-foreground" />
              </div>
              {mode !== "forgot" && (
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-background text-foreground" />
                </div>
              )}
              <button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground py-3 rounded-sm font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
                {submitting ? "Please wait..." : mode === "signin" ? "Sign In" : mode === "signup" ? "Create Account" : "Send Reset Link"}
              </button>
            </form>

            <div className="mt-6 text-center space-y-2">
              {mode === "signin" && (
                <>
                  <button onClick={() => setMode("forgot")} className="text-sm text-muted-foreground hover:text-foreground transition-colors">Forgot password?</button>
                  <p className="text-xs text-muted-foreground">
                    Don't have an account?{" "}
                    <button onClick={() => setMode("signup")} className="text-primary hover:underline">Request access</button>
                  </p>
                </>
              )}
              {mode !== "signin" && (
                <button onClick={() => setMode("signin")} className="text-sm text-primary hover:underline">Back to sign in</button>
              )}
            </div>

            <p className="text-xs text-muted-foreground mt-6 text-center">
              Access is restricted to current clients of Batts Legal LLP.
            </p>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Auth;
